import React, { useState } from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { TSlotCard } from '@lootboy/lib/slices/cardCollections/hooks/useSlotCards';

import ButtonClean from '~/components/ButtonClean';
import Image from '~/components/Image';
import { ESnackbarVariant } from '~/components/molecules/Snackbar';
import SnackbarMarkdown from '~/components/molecules/Snackbar/SnackbarMarkdown';
import { useSnackbar } from '~/components/organisms/SnackbarContext';

import { ReactComponent as CheckIcon } from '~/assets/images/icons/i-check-circle.svg';

import cardStyles from '../CardTile.module.scss';
import styles from './RequiredCardDialogSelection.module.scss';

interface IRequiredCardDialogContentProps {
  onCardClick: (cardId: string) => void;
  cards: Array<TSlotCard>;
  selectedCard: { _id: string; image: string } | undefined;
}

const RequiredCardDialogContent: React.FC<IRequiredCardDialogContentProps> = ({
  onCardClick,
  cards,
  selectedCard,
}) => {
  const [isCardSelected, setCardSelected] = useState(Boolean(selectedCard));
  const { pushSnackbar, dismissSnackbar } = useSnackbar();

  const specialCard = selectedCard
    ? { ...selectedCard, isAvailable: true, placedInCollection: null }
    : cards[0];

  const markCardAsClicked = (cardId: string) => {
    // card is in another collection and is highlighted yellow
    if (isCardSelected && !specialCard.isAvailable) {
      dismissSnackbar();
      setCardSelected(false);
      return;
    }
    // card is in another collection and is not clicked
    if (
      !isCardSelected &&
      specialCard.placedInCollection &&
      !specialCard.isAvailable
    ) {
      pushSnackbar({
        variant: ESnackbarVariant.WARNING,
        onClose: () => {
          setCardSelected(false);
        },
        children: (
          <SnackbarMarkdown>
            {I18n.t('collections_sb_card_already_placed', {
              collectionId: specialCard.placedInCollection,
            })}
          </SnackbarMarkdown>
        ),
      });
      setCardSelected(true);
      return;
    }
    // handle enabled cards
    setCardSelected(!selectedCard);
    onCardClick(cardId);
  };

  return (
    <div className={styles.cardsContainer}>
      <h3>{I18n.t('collections_bs_selector_headline')}</h3>
      <ButtonClean
        className={classNames(cardStyles.card, styles.specialGroupCard, {
          [cardStyles.selected]: isCardSelected,
          [cardStyles.disabled]: !specialCard.isAvailable,
          [cardStyles.placed]:
            isCardSelected &&
            !specialCard.isAvailable &&
            specialCard.placedInCollection,
        })}
        disabled={!specialCard.isAvailable && !specialCard.placedInCollection}
        onClick={() => {
          markCardAsClicked(specialCard._id);
        }}
      >
        <Image
          alt="Special group card graphic"
          path={specialCard.image}
        />
        <CheckIcon />
      </ButtonClean>
    </div>
  );
};

export default RequiredCardDialogContent;
